/**
 * Название и адрес организации: разбор ввода и сборка патча.
 *
 * Отдельно от `rules.ts`, потому что это разные экраны и разная цена ошибки: правила
 * управляют антиспамом, а здесь — витрина. Общее у них одно — состав патча, за которым
 * следят тесты обоих файлов.
 */

import type { CabinetBusinessDto, UpdateBusinessInput } from '../../../api/cabinetTypes';

/** Границы повторяют `validName` / `validAddress` бэкенда — см. пояснение в `rules.ts`. */
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 120;
export const ADDRESS_MAX_LENGTH = 200;

export interface ProfileDraft {
  readonly name: string;
  readonly address: string;
  /**
   * Город и район — id из справочника `@app/shared`, `null` — не выбран. Необязательные:
   * черновики в тестах патча про них не знают, и их отсутствие означает «это поле не
   * трогаем» (см. `buildProfilePatch`). Экран всегда задаёт их через `profileDraftFrom`.
   */
  readonly city?: string | null;
  readonly district?: string | null;
}

export function profileDraftFrom(business: CabinetBusinessDto): ProfileDraft {
  // `address: null` — «адрес не указан», и в поле ввода это пустая строка, а не текст «null».
  return {
    name: business.name,
    address: business.address ?? '',
    city: business.city ?? null,
    district: business.district ?? null,
  };
}

export type ProfileIssue = 'nameShort' | 'nameLong' | 'addressLong';

export type ProfileErrors = Readonly<Partial<Record<'name' | 'address', ProfileIssue>>>;

export type ProfilePatchResult =
  | { readonly ok: true; readonly patch: UpdateBusinessInput; readonly changed: boolean }
  | { readonly ok: false; readonly errors: ProfileErrors };

/**
 * Патч профиля.
 *
 * `slug` и `type` не редактируются и в патч не попадают: их нет в `UpdateBusinessInput`,
 * а бэкенд отвергает их в теле, а не игнорирует. Slug печатается на QR-наклейках
 * у клиентов и не меняется никогда — даже при переименовании организации.
 *
 * Пустой адрес превращается в `null`, а не в пустую строку: это нормализация ввода
 * (очистить адрес обязано быть возможно), а не подстановка значения по умолчанию.
 * Обрезка пробелов по краям — там же и по той же причине: «  Ташкент » и «Ташкент»
 * не должны считаться разными адресами и порождать пустое сохранение.
 */
export function buildProfilePatch(
  current: CabinetBusinessDto,
  draft: ProfileDraft,
): ProfilePatchResult {
  const name = draft.name.trim();
  const address = draft.address.trim();
  const errors: Record<string, ProfileIssue> = {};

  if (name.length < NAME_MIN_LENGTH) errors['name'] = 'nameShort';
  else if (name.length > NAME_MAX_LENGTH) errors['name'] = 'nameLong';
  if (address.length > ADDRESS_MAX_LENGTH) errors['address'] = 'addressLong';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const patch: UpdateBusinessInput = {};
  if (name !== current.name) patch.name = name;

  const nextAddress = address.length === 0 ? null : address;
  if (nextAddress !== (current.address ?? null)) patch.address = nextAddress;

  // Город/район попадают в патч, только если черновик их вообще несёт (`!== undefined`):
  // так старые тесты патча без этих полей не превращаются в «очистить город». Связность
  // пары (район принадлежит городу) проверяет бэкенд; пикер присылает её согласованной.
  if (draft.city !== undefined && draft.city !== (current.city ?? null)) patch.city = draft.city;
  if (draft.district !== undefined && draft.district !== (current.district ?? null)) {
    patch.district = draft.district;
  }

  return { ok: true, patch, changed: Object.keys(patch).length > 0 };
}
